import get from "lodash/get";
import { detect } from "detect-browser";
import { MediaStreamRecorder, RecordRTCPromisesHandler, StereoAudioRecorder } from "recordrtc";
import { call, cancelled, delay, put, race, select, take, takeLatest } from "redux-saga/effects";
import { endFlow, startFlow } from "../../actions/flow";
import { changeFormValue } from "../../actions/form";
import getForm from "../../selectors/getForm";
import { getFlow, getFlowEnd, getFlowStart } from "../../selectors/flow";
import { getEntityById } from "../../selectors/entity";
import { getScore } from "../../services/speechRecognitionService";
import {
  CLOSE_CERTIFICATION_TEST_ABILITY_EXECUTION_FLOW,
  ITEM_EXECUTION_FORM,
  OLD_END_RECORD_BY_TIMEOUT_FLOW,
  OLD_END_RECORD_FLOW,
  OLD_START_RECORD_FLOW,
  SAVE_CERTIFICATION_TEST_ABILITY_ITEM_EXECUTION_ANSWER_FLOW,
  SYSTEM_MESSAGE_FLOW,
} from "../../consts";
import { insertSpeechRecognitionAudio, stopAudio } from "../../stores/audio-store";
import { logError } from "../../util";

const info = detect();
let recordStream = null;
let recorderInstance = null;

export function* startOldRecordFlow() {
  yield takeLatest(getFlowStart(OLD_START_RECORD_FLOW), function* () {
    yield race({
      cancelCertification: take(getFlowStart(CLOSE_CERTIFICATION_TEST_ABILITY_EXECUTION_FLOW)),
      call: call(function* () {
        try {
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", true));

          recordStream = yield new Promise((resolve, reject) => {
            if (navigator.mediaDevices) {
              navigator.mediaDevices
                .getUserMedia({ audio: true, video: false })
                .then((stream) => resolve(stream))
                .catch((error) => reject(error));
            } else {
              (
                navigator.getUserMedia ||
                navigator.webkitGetUserMedia ||
                navigator.mozGetUserMedia ||
                navigator.msGetUserMedia
              )(
                { audio: true, video: false },
                (stream) => resolve(stream),
                (error) => reject(error)
              );
            }
          });

          const itemExecutionForm = yield select(getForm(ITEM_EXECUTION_FORM));

          recorderInstance = new RecordRTCPromisesHandler(recordStream, {
            type: "audio",
            numberOfAudioChannels: 1,
            recorderType: info.name === "safari" ? StereoAudioRecorder : MediaStreamRecorder,
            disableLogs: true,
          });
          yield recorderInstance.startRecording();

          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isRecording", true));
          yield put(
            startFlow(OLD_END_RECORD_BY_TIMEOUT_FLOW, {
              time: itemExecutionForm.values.associativeItem.timeToAutoEndRecord,
            })
          );
        } catch (error) {
          if (error.name === "PermissionDeniedError" || error.name === "NotAllowedError") {
            yield put(
              startFlow(SYSTEM_MESSAGE_FLOW, {
                message: "error.error_record_not_allowed",
              })
            );
          } else if (error.message === "Requested device not found") {
            yield put(
              startFlow(SYSTEM_MESSAGE_FLOW, {
                message: "error.error_record_missing_mic",
              })
            );
          } else {
            logError({ error, flow: OLD_START_RECORD_FLOW });
            yield put(
              startFlow(SYSTEM_MESSAGE_FLOW, {
                message: "error.error_start_record",
              })
            );
          }
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isRecording", false));
          if (recorderInstance) {
            recorderInstance.stopRecording();
            yield recorderInstance.destroy();

            recordStream.getAudioTracks().forEach((track) => track.stop());
          }
        } finally {
          if (yield cancelled()) {
            if (recorderInstance) {
              recorderInstance.stopRecording();
              recorderInstance.clearRecordedData();
            }
          }
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", false));
          yield put(endFlow(OLD_START_RECORD_FLOW));
        }
      }),
    });
  });
}

export function* endOldRecordFlow() {
  yield takeLatest(getFlowStart(OLD_END_RECORD_FLOW), function* () {
    yield race({
      cancelCertification: take(getFlowStart(CLOSE_CERTIFICATION_TEST_ABILITY_EXECUTION_FLOW)),
      call: call(function* () {
        try {
          yield recorderInstance.stopRecording();
          recordStream.getAudioTracks().forEach((track) => track.stop());

          const recordBlob = yield recorderInstance.getBlob();
          const recordUrl = yield recorderInstance.getDataURL();

          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isSubmittingRecord", true));
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isRecording", false));

          const itemExecutionForm = yield select(getForm(ITEM_EXECUTION_FORM));
          const currentItem = itemExecutionForm.values.associativeItem.item;

          if (recordBlob.size === 0) {
            yield put(
              startFlow(SYSTEM_MESSAGE_FLOW, {
                message: "error.noSpeechDetected",
              })
            );
            return;
          }

          yield call(insertSpeechRecognitionAudio, {
            data: recordUrl,
            isDeletable: true,
          });
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "recordFile", recordBlob));
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "recordSound", recordUrl));

          const id = sessionStorage.getItem("id");
          const profile = yield select(getEntityById("profile", id));

          if (["FREE_SPEAK", "FREE_SPEAK_IMAGE"].some((type) => type === currentItem.type.key)) {
            yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", true));
            if (currentItem.type.key === "FREE_SPEAK") {
              try {
                const speechRecognitionResult = yield call(getScore, {
                  id,
                  isDemoStudent: !!profile.isDemoStudent,
                  isStagingEnvironment: process.env.REACT_APP_ENVIRONMENT !== "production",
                  record: recordBlob,
                  text: currentItem.text,
                  wordsDictionary: currentItem.speechRecognitionDictionary || {},
                  allowSpeechRecognitionBonus: false,
                  passUnknownWordsError: true,
                });

                // check if student recorded the item text instead of reply
                if (speechRecognitionResult.qualityScore >= 75) {
                  yield put(changeFormValue(ITEM_EXECUTION_FORM, "isSubmittingRecord", false));
                  yield put(
                    startFlow(SYSTEM_MESSAGE_FLOW, {
                      message: "error.error_reply_ct_speak",
                      button: "certificationTest.help.gotIt",
                    })
                  );
                  yield take(getFlowEnd(SYSTEM_MESSAGE_FLOW));
                  yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", false));
                  return;
                }
              } catch (error) {
                if (error.code === "error_no_speech") {
                  yield put(changeFormValue(ITEM_EXECUTION_FORM, "isSubmittingRecord", false));
                  yield put(
                    startFlow(SYSTEM_MESSAGE_FLOW, {
                      message: "error.noSpeechDetected",
                    })
                  );
                  yield take(getFlowEnd(SYSTEM_MESSAGE_FLOW));
                  yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", false));
                  return;
                }
              }
            }

            yield put(changeFormValue(ITEM_EXECUTION_FORM, "answer", recordBlob));
            yield put(startFlow(SAVE_CERTIFICATION_TEST_ABILITY_ITEM_EXECUTION_ANSWER_FLOW));
            yield put(changeFormValue(ITEM_EXECUTION_FORM, "isSubmittingRecord", false));
            return;
          }

          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", false));
        } catch (error) {
          if (typeof error === "string" && error.indexOf("Empty blob") > -1) {
            yield put(
              startFlow(SYSTEM_MESSAGE_FLOW, {
                message: "error.noSpeechDetected",
              })
            );
          } else {
            logError({ error, flow: OLD_END_RECORD_FLOW });
            yield put(
              startFlow(SYSTEM_MESSAGE_FLOW, {
                message: "error.error_stop_record",
              })
            );
          }
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isRecording", false));
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isSubmittingRecord", false));
          yield put(changeFormValue(ITEM_EXECUTION_FORM, "isDisabled", false));
        } finally {
          if (yield cancelled()) {
            stopAudio();
          }

          yield recorderInstance.destroy();
          yield put(endFlow(OLD_END_RECORD_FLOW));
        }
      }),
    });
  });
}

export function* endOldRecordByTimeoutFlow() {
  yield takeLatest(getFlowStart(OLD_END_RECORD_BY_TIMEOUT_FLOW), function* () {
    const flow = yield select(getFlow(OLD_END_RECORD_BY_TIMEOUT_FLOW));
    const raceWinner = yield race({
      timeout: delay(get(flow.params, "time", 60000) || 60000),
      exitCertification: take(getFlowStart(CLOSE_CERTIFICATION_TEST_ABILITY_EXECUTION_FLOW)),
      userStop: take(getFlowStart(OLD_END_RECORD_FLOW)),
    });
    if (raceWinner.timeout) {
      yield put(startFlow(OLD_END_RECORD_FLOW));
    }
  });
}
